/**
 * @fileoverview Module for `src/lib/segment.js`.
 */

/**
 * Segment tracking helpers.
 *
 * Wraps common Segment browser SDK calls behind null-safe helpers so calling
 * code can track events without repeatedly checking SDK availability.
 */

/**
 * Wraps an event handler to track a Segment event before running it.
 *
 * @param {Function} wrappedFunction - Original event handler.
 * @param {string} event - Segment event name.
 * @param {Object} [properties] - Additional event properties.
 * @returns {Function} Wrapped handler.
 */
export function segmentTrackWrapper(wrappedFunction, event, properties) {
  return (e) => {
    if (window?.analytics?.track) {
      window.analytics.track(event, properties);
    }
    wrappedFunction(e);
  };
}

/**
 * Tracks an event in Segment when analytics is available.
 *
 * @param {string} eventId - Segment event identifier.
 * @param {Object} [params] - Event payload.
 */
export function segmentTrack(eventId, params) {
  if (window?.analytics?.track) {
    window.analytics.track(eventId, params);
  }
}

/**
 * Identifies a user in Segment if no user has been identified yet.
 *
 * @param {string} userId - Application user identifier.
 */
export function segmentIdentify(userId) {
  if (window?.analytics && window?.analytics?.user) {
    const segmentId = window.analytics.user().id();
    if (!segmentId) {
      window.analytics.identify(userId);
    }
  }
}

/**
 * Identifies the user (if provided) and records a page view in Segment.
 *
 * @param {string} [userId] - Application user identifier.
 */
export function segmentIdentifyAndTrackPage(userId) {
  if (userId) {
    segmentIdentify(userId);
  }
  if (window?.analytics) {
    window.analytics.page();
  }
}
